/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { ChatMessage, ChatSession } from 'types/api/textql/textql'

type AiReportsChatState = {
  sessions: Record<string, ChatSession>
  activeSessionId: string | null
}

type AddMessagePayload = {
  sessionId: string
  message: ChatMessage
  title?: string
  // When creating a session, whether it also becomes the active one (default true). Pass false
  // when an answer resolves after the user already navigated to another chat.
  activate?: boolean
}

const initialState: AiReportsChatState = {
  sessions: {},
  activeSessionId: null,
}

const persistentAiReportsChatSlice = createSlice({
  name: 'persistentAiReportsChat',
  initialState,
  reducers: {
    createNewSession: state => {
      state.activeSessionId = null
    },
    setActiveSession: (state, action: PayloadAction<string>) => {
      state.activeSessionId = action.payload
    },
    addMessageToSession: (state, action: PayloadAction<AddMessagePayload>) => {
      const { sessionId, message, title, activate = true } = action.payload
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = {
          id: sessionId,
          title: title || 'New Chat',
          messages: [],
          createdAt: new Date().toISOString(),
        }
        if (activate) {
          state.activeSessionId = sessionId
        }
      }
      state.sessions[sessionId].messages.push(message)
    },
    renameSession: (state, action: PayloadAction<{ sessionId: string; title: string }>) => {
      const session = state.sessions[action.payload.sessionId]
      if (session) {
        session.title = action.payload.title
      }
    },
    resetState: () => initialState,
  },
})

export const { createNewSession, setActiveSession, addMessageToSession, renameSession, resetState } = persistentAiReportsChatSlice.actions

type SliceRoot = { persistentAiReportsChat: AiReportsChatState }

export const selectAiChatSessions = (state: SliceRoot) => state.persistentAiReportsChat.sessions

export const selectActiveSessionId = (state: SliceRoot) => state.persistentAiReportsChat.activeSessionId

export default persistentAiReportsChatSlice.reducer
